import { RequestStatus } from 'dg-web-shared/lib/ServerRequestStateSlices.ts';
import { Spinner } from '../ui/loaders/Spinner.tsx';
import { useSearchParams, useNavigate } from 'react-router';
import { useEffect, useState, useRef } from 'react';
import { languageFromString, Language } from 'dg-web-shared/lib/Text.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { TextField, Box, Stack } from '@mui/material';
import {
    useServerWrite,
    ErrorState,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { TurnstileAction } from 'dg-web-shared/lib/TurnstileActions';
import { PasswordChangeTokenSource } from '../password-change-token/ChangePasswordController.tsx';

export type ResetPasswordResponseFailure =
    | ResponseNoEmailForUser
    | ResponseNoEmailForAdminUser;

export type ResetPasswordResponseSuccess = object;

interface ResponseNoEmailForUser {
    code: ResponseFailureCode.NO_EMAIL_FOR_USER;
    organizationAdminName: string;
}

interface ResponseNoEmailForAdminUser {
    code: ResponseFailureCode.NO_EMAIL_FOR_ADMIN_USER;
}

export enum ResponseFailureCode {
    NO_EMAIL_FOR_USER = 'NO_EMAIL_FOR_USER',
    NO_EMAIL_FOR_ADMIN_USER = 'NO_EMAIL_FOR_ADMIN_USER',
}

export function ResetPassword({
    source,
}: {
    source: PasswordChangeTokenSource;
}) {
    const navigate = useNavigate();
    const [resetPasswordState, resetPassword] = useServerWrite<
        {
            username: string;
            language: Language;
            source: PasswordChangeTokenSource;
            turnstileToken: string | null;
        },
        null,
        ResetPasswordResponseFailure
    >(() => ({
        url: `/ui-api/operator-account/reset-password`,
    }));

    const [searchParams] = useSearchParams();
    const { language, setLanguage } = useLanguage();

    const languageFromParam = languageFromString(
        searchParams.get('language') ?? 'de',
    );

    const turnstileRef = useRef<TurnstileInstance | null>(null);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    useEffect(() => {
        if (language !== languageFromParam) {
            setLanguage(languageFromParam);
        }
    }, [language, languageFromParam, setLanguage]);

    const [username, setUsername] = useState(
        searchParams.get('username') ?? '',
    );
    const [hasEdited, setHasEdited] = useState(false);

    function submit() {
        if (isValid(username)) {
            resetPassword({
                username: username,
                language: languageFromParam,
                source,
                turnstileToken,
            });
        }
    }

    const disabled = !isValid(username) || turnstileToken == null;

    function close() {
        switch (source) {
            case PasswordChangeTokenSource.APP:
                return;
            case PasswordChangeTokenSource.WEB:
                return navigate('/');
            case PasswordChangeTokenSource.MOBILE:
                return navigate('/mobile');
        }
    }

    switch (resetPasswordState.status) {
        case RequestStatus.PENDING:
            return <Spinner loading={true} />;
        case RequestStatus.SUCCESS:
            return (
                <ConfirmationDialog
                    title={
                        <Localized
                            de="Anfrage erhalten"
                            fr="Demande reçue"
                            it="Richiesta ricevuta"
                            en="Request received"
                        />
                    }
                    open={true}
                    dialogVersion={ConfirmationDialogVersions.SUCCESS}
                    confirmLabel={
                        <Localized
                            de="Schliessen"
                            fr="Fermer"
                            it="Chiudi"
                            en="Close"
                        />
                    }
                    onConfirm={close}
                >
                    <p>
                        <Localized
                            de={`Wir haben Ihre Anfrage erhalten; falls der Benutzername "${username}" existiert, werden wir an die zugehörige E-Mail-Adresse einen Link senden.`}
                            fr={`Nous avons reçu votre demande. Si le nom d'utilisateur "${username}" existe, nous allons vous envoyer un lien à l'adresse e-mail associée.`}
                            it={`Abbiamo ricevuto la sua richiesta; se il nome utente "${username}" esiste, invieremo un link all'indirizzo e-mail associato.`}
                            en={`We have received your request; if the username "${username}" exists, we will send a link to the associated email address.`}
                        />
                    </p>
                </ConfirmationDialog>
            );
        case RequestStatus.ERROR:
            return (
                <ConfirmationDialog
                    title={
                        <Localized
                            de="Anfrage fehlgeschlagen"
                            fr="Demande non reçue"
                            it="Richiesta non ricevuta"
                            en="Request failed"
                        />
                    }
                    open={true}
                    dialogVersion={ConfirmationDialogVersions.ERROR}
                    confirmLabel={
                        <Localized
                            de="Schliessen"
                            fr="Fermer"
                            it="Chiudi"
                            en="Close"
                        />
                    }
                    onConfirm={close}
                >
                    <p>{failureMessage(username, resetPasswordState)}</p>
                </ConfirmationDialog>
            );

        case RequestStatus.NEVER_EXECUTED:
            return (
                <ConfirmationDialog
                    title={
                        <Localized
                            de="Passwort zurücksetzen"
                            fr="Réinitialiser MDP"
                            it="Reimposta password"
                            en="Reset password"
                        />
                    }
                    open={true}
                    fullWidth={false}
                    dialogVersion={ConfirmationDialogVersions.PRIMARY}
                    confirmLabel={
                        <Localized
                            de="Bestätigen"
                            fr="Confirmer"
                            it="Conferma"
                            en="Confirm"
                        />
                    }
                    onConfirm={submit}
                    onCancel={close}
                    confirmDisabled={disabled}
                    closeDisabled={true}
                >
                    <Box sx={{ marginY: 3 }}>
                        <form
                            onSubmit={event => {
                                event.preventDefault();
                                submit();
                            }}
                        >
                            <Stack spacing={3}>
                                <TextField
                                    value={username}
                                    label={
                                        <Localized
                                            de="Benutzername"
                                            fr="Nom d'utilisateur"
                                            it="Nome utente"
                                            en="Username"
                                        />
                                    }
                                    onBlur={() => setHasEdited(true)}
                                    type="text"
                                    error={hasEdited && !isValid(username)}
                                    helperText={
                                        hasEdited && !isValid(username) ? (
                                            <Localized
                                                de="Pflichtfeld"
                                                fr="Champ obligatoire"
                                                it="Campo obbligatorio"
                                                en="Required field"
                                            />
                                        ) : (
                                            ''
                                        )
                                    }
                                    fullWidth
                                    onChange={event =>
                                        setUsername(event.target.value)
                                    }
                                />
                                <Box>
                                    <Turnstile
                                        ref={turnstileRef}
                                        siteKey={'0x4AAAAAABatiRYriPvAMuT9'}
                                        options={{
                                            theme: 'light',
                                            size: 'flexible',
                                            action: TurnstileAction.OPERATOR_RESET_PASSWORD,
                                        }}
                                        onError={() => setTurnstileToken(null)}
                                        onExpire={() => setTurnstileToken(null)}
                                        onSuccess={token =>
                                            setTurnstileToken(token)
                                        }
                                    />
                                </Box>

                                <button
                                    type="submit"
                                    hidden
                                    disabled={disabled}
                                />
                            </Stack>
                        </form>
                    </Box>
                </ConfirmationDialog>
            );
    }
}

function isValid(username: string): boolean {
    return username.length > 0;
}

function failureMessage(
    username: string,
    remoteState: ErrorState<ResetPasswordResponseFailure>,
) {
    if (remoteState.status !== RequestStatus.ERROR) {
        throw Error('Do not call failure messages without error');
    }

    if (remoteState.data === null || !remoteState.data.code) {
        return (
            <Localized
                de={`Die Anfrage konnte nicht übermittelt werden (HTTP ${remoteState.httpStatusCode}). Bitte versuchen Sie später nochmals.`}
                fr={`La requête n'a pas pu être envoyée (HTTP ${remoteState.httpStatusCode}). Veuillez réessayer plus tard.`}
                it={`La richiesta non ha potuto essere inviata (HTTP ${remoteState.httpStatusCode}). Vogliate riprovare più tardi.`}
                en={`The request could not be sent (HTTP ${remoteState.httpStatusCode}). Please try again later.`}
            />
        );
    }

    switch (remoteState.data.code) {
        case ResponseFailureCode.NO_EMAIL_FOR_USER:
            return (
                <Localized
                    de={`Wir können den Vorgang zum Zurücksetzen des Kennworts nicht starten, da beim Benutzer "${username}" keine E-Mail-Adresse hintergelegt ist. Bitte wenden Sie sich an Ihren Administrator (${remoteState.data.organizationAdminName}).`}
                    fr={`Nous ne pouvons pas démarrer le processus de réinitialisation du mot de passe car pour l'utilisateur "${username}" il y a aucune adresse e-mail configurée. Veuillez contacter votre administrateur (${remoteState.data.organizationAdminName}).`}
                    it={`Non è possibile avviare la procedura di reimpostazione della password perché per l'utente "${username}" non è configurato alcun indirizzo e-mail. Vogliate di contattare il vostro amministratore (${remoteState.data.organizationAdminName}).`}
                    en={`We cannot start the password reset process for user "${username}" because no email address is configured. Please contact your administrator (${remoteState.data.organizationAdminName}).`}
                />
            );

        case ResponseFailureCode.NO_EMAIL_FOR_ADMIN_USER:
            return (
                <Localized
                    de={`Wir können den Vorgang zum Zurücksetzen des Kennworts nicht starten, da beim Benutzer "${username}" keine E-Mail-Adresse hintergelegt ist. Bitte wenden Sie sich an unseren Parkingportal-Helpdesk.`}
                    fr={`Nous ne pouvons pas démarrer le processus de réinitialisation du mot de passe car pour l'utilisateur "${username}" il y a aucune adresse e-mail configurée. Veuillez contacter notre helpdesk Parkingportal.`}
                    it={`Non è possibile avviare la procedura di reimpostazione della password perché per l'utente "${username}" non è configurato alcun indirizzo e-mail. Vogliate di contattare il nostro helpdesk Parkingportal.`}
                    en={`We cannot start the password reset process for user "${username}" because no email address is configured. Please contact our Parkingportal helpdesk.`}
                />
            );
    }
}
